import React, { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  useGetPlatformAdminOrganisationQuery,
  useFixTwilioCredentialsMutation,
  useFixMobileCredentialsMutation,
  useSyncTwimlUrlMutation,
  useSyncSmsUrlMutation,
  useToggleA2pMockBrandMutation,
  useSetBillingExemptMutation,
  useAssignPlanMutation,
  useRevokePlanMutation,
  useGetOrganisationSubscriptionQuery,
  useClearPlanInvitationMutation,
  useGetSubscriptionPlansQuery,
  useAddOrganisationCreditMutation,
} from "@/store/admin_api";
import { apiErrorMessage } from "@/util/apiError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FaArrowLeft,
  FaCheck,
  FaLink,
  FaMobileAlt,
  FaReceipt,
  FaRobot,
  FaSms,
  FaTimes,
  FaWrench,
} from "react-icons/fa";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import AssignAgentDialog from "@/components/pages/platform-admin/ai-agents/components/AssignAgentDialog";
import OrganisationSettingsTab from "@/components/pages/platform-admin/organisations/OrganisationSettingsTab";
import OrganisationWorkflowsTab from "@/components/pages/platform-admin/organisations/OrganisationWorkflowsTab";
import OrganisationInvoicesTab from "@/components/pages/platform-admin/organisations/OrganisationInvoicesTab";
import Organisation from "@/model/interface/Organisation";

const SUBSCRIPTION_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  TRIAL: "default",
  ACTIVE: "default",
  PAST_DUE: "destructive",
  TRIAL_LAPSED: "destructive",
  CANCELED: "secondary",
  SUSPENDED: "destructive",
};

function OverviewRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b py-3 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{children}</span>
    </div>
  );
}

function ActionRow({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-6 rounded-lg border p-4">
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export default function OrganisationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: organisation, isLoading, refetch } = useGetPlatformAdminOrganisationQuery(id ?? "", { skip: !id });
  const [fixTwilio, { isLoading: isFixing }] = useFixTwilioCredentialsMutation();
  const [fixMobile, { isLoading: isFixingMobile }] = useFixMobileCredentialsMutation();
  const [syncTwimlUrl, { isLoading: isSyncingTwiml }] = useSyncTwimlUrlMutation();
  const [syncSmsUrl, { isLoading: isSyncingSms }] = useSyncSmsUrlMutation();
  const [toggleA2pMock, { isLoading: isTogglingMock }] = useToggleA2pMockBrandMutation();
  const [setBillingExempt, { isLoading: isTogglingExempt }] = useSetBillingExemptMutation();
  const [assignPlan, { isLoading: isAssigningPlan }] = useAssignPlanMutation();
  const [revokePlan, { isLoading: isRevokingPlan }] = useRevokePlanMutation();
  const [clearPlanInvitation, { isLoading: isClearingInvitation }] = useClearPlanInvitationMutation();
  const [addCredit, { isLoading: isAddingCredit }] = useAddOrganisationCreditMutation();
  const { data: subscriptionPlans } = useGetSubscriptionPlansQuery();
  const { data: organisationSubscription } = useGetOrganisationSubscriptionQuery(id ?? "", {
    skip: !id || !organisation?.stripeSubscriptionId,
  });
  const [assignAgentOpen, setAssignAgentOpen] = useState(false);
  const [planToAssign, setPlanToAssign] = useState<string>("");
  const [validUntil, setValidUntil] = useState<string>("");
  const [creditAmount, setCreditAmount] = useState<string>("");
  const [creditReason, setCreditReason] = useState<string>("");

  const tab = searchParams.get("tab") ?? "overview";
  const privatePlans = (subscriptionPlans ?? []).filter((plan) => plan.visibility === "PRIVATE" && plan.isActive);
  const activePlanDetails = (subscriptionPlans ?? []).find(
    (plan) => plan.slug === organisation?.activePlan?.toLowerCase(),
  );
  const isOnPrivatePlan = activePlanDetails?.visibility === "PRIVATE";
  const billingDate = organisationSubscription?.currentPeriodEnd
    ? format(new Date(organisationSubscription.currentPeriodEnd * 1000), "MMM d, yyyy")
    : null;
  const planReturnLabel = (() => {
    const planReturn = organisation?.planReturn;
    if (!planReturn) {
      return null;
    }
    if (planReturn.plan) {
      return `${planReturn.planName ?? planReturn.plan}, ${planReturn.interval === "ANNUAL" ? "annual" : "monthly"}`;
    }
    if (planReturn.status === "CANCELED" || planReturn.status === "SUSPENDED") {
      return `a ${planReturn.status.toLowerCase()} subscription`;
    }
    const trialEnd = organisation?.trialExpirationDate ? new Date(organisation.trialExpirationDate) : null;
    return trialEnd && trialEnd > new Date()
      ? `the trial, until ${format(trialEnd, "MMM d, yyyy")}`
      : "the trial, already expired";
  })();

  const setTab = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  const handleAssignPlan = async () => {
    if (!organisation || !planToAssign) {
      return;
    }
    try {
      const result = await assignPlan({ organisationId: organisation.id, plan: planToAssign, validUntil }).unwrap();
      const returnNote = result.returnsTo ? ` It returns to ${result.returnsTo}${validUntil ? ` on ${validUntil}` : " when revoked"}.` : "";
      if (result.subscribed) {
        toast.success(`Plan assigned and the subscription is active.${returnNote}`);
      } else {
        toast.info(`Invitation saved. The organisation still needs: ${result.missing.join(", ")}.${returnNote}`);
      }
      refetch();
    } catch (error) {
      toast.error((error as { data?: { detail?: string } })?.data?.detail || "Could not assign the plan");
    }
  };

  const handleRevokePlan = async () => {
    if (!organisation) {
      return;
    }
    try {
      const result = await revokePlan(organisation.id).unwrap();
      toast.success(`Returned to ${result.returnsTo ?? "the previous plan"}`);
      refetch();
    } catch (error) {
      toast.error((error as { data?: { detail?: string } })?.data?.detail || "Could not revoke the plan");
    }
  };

  const handleAddCredit = async () => {
    if (!organisation) {
      return;
    }
    try {
      await addCredit({ organisationId: organisation.id, amount: creditAmount, reason: creditReason.trim() }).unwrap();
      toast.success(`$${Number(creditAmount).toFixed(2)} credit added`);
      setCreditAmount("");
      setCreditReason("");
      refetch();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not add the credit"));
    }
  };

  const runAction = async (action: () => Promise<unknown>, successMessage: string) => {
    try {
      await action();
      toast.success(successMessage);
      refetch();
    } catch (error) {
      toast.error(
        (error as { data?: { detail?: string; message?: string } })?.data?.detail ||
          (error as { data?: { message?: string } })?.data?.message ||
          "Action failed",
      );
    }
  };

  if (isLoading || !organisation) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
          <Link to="/platform-admin/organisations">
            <FaArrowLeft className="mr-2" />
            All organisations
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-bold">{organisation.name}</h2>
          <Badge variant={SUBSCRIPTION_VARIANTS[organisation.subscriptionStatus] || "outline"}>
            {organisation.subscriptionStatus.replace("_", " ")}
          </Badge>
          {organisation.billingExempt && <Badge variant="outline">Billing exempt</Badge>}
        </div>
        <p className="font-mono text-sm text-muted-foreground">{organisation.id}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <OrganisationSettingsTab organisationId={organisation.id} />
        </TabsContent>

        <TabsContent value="workflows">
          <OrganisationWorkflowsTab organisationId={organisation.id} />
        </TabsContent>

        <TabsContent value="invoices">
          <OrganisationInvoicesTab organisationId={organisation.id} />
        </TabsContent>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <OverviewRow label="Plan">
                {activePlanDetails ? (
                  <>
                    {activePlanDetails.name}
                    {activePlanDetails.pricingMode === "AT_COST" ? (
                      <span className="ml-2 text-muted-foreground">
                        <span className="line-through">${activePlanDetails.monthlyBasePrice}/mo</span> $0/mo, add-ons at
                        cost
                      </span>
                    ) : (
                      <span className="ml-2 text-muted-foreground">${activePlanDetails.monthlyBasePrice}/mo</span>
                    )}
                  </>
                ) : (
                  (organisation.activePlan ?? "—")
                )}
              </OverviewRow>
              {organisation.invitedPlan && organisation.invitedPlan !== organisation.activePlan && (
                <OverviewRow label="Invited to">{organisation.invitedPlan}</OverviewRow>
              )}
              {organisation.planValidUntil && (
                <OverviewRow label="Valid until">{format(parseISO(organisation.planValidUntil), "MMM d, yyyy")}</OverviewRow>
              )}
              {planReturnLabel && <OverviewRow label="Returns to">{planReturnLabel}</OverviewRow>}
              <OverviewRow label="Billing interval">{organisation.billingInterval ?? "MONTHLY"}</OverviewRow>
              <OverviewRow label="Balance">${organisation.balance?.toFixed(2) ?? "0.00"}</OverviewRow>
              <OverviewRow label="KYC status">{organisation.kycVerificationStatus.replace("_", " ")}</OverviewRow>
              <OverviewRow label="Valid phone">
                {organisation.validPhone ? <FaCheck className="text-green-600" /> : <FaTimes className="text-red-600" />}
              </OverviewRow>
              <OverviewRow label="Valid email">
                {organisation.validEmail ? <FaCheck className="text-green-600" /> : <FaTimes className="text-red-600" />}
              </OverviewRow>
              {organisation.subscriptionStatus === "PAST_DUE" && organisation.subscriptionGraceUntil && (
                <OverviewRow label="Grace until">
                  {format(new Date(organisation.subscriptionGraceUntil), "MMM dd, yyyy HH:mm")}
                </OverviewRow>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="actions">
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
              <CardDescription>Administrative operations for this organisation.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ActionRow
                title="Billing exempt"
                description="Free access: skips subscription enforcement, dunning, and the nightly Stripe reconciliation."
              >
                <Switch
                  checked={!!organisation.billingExempt}
                  disabled={isTogglingExempt}
                  onCheckedChange={(checked) =>
                    runAction(
                      () => setBillingExempt({ organisationId: organisation.id, exempt: checked }).unwrap(),
                      `Billing exempt ${checked ? "enabled" : "disabled"}`,
                    )
                  }
                />
              </ActionRow>

              <ActionRow
                title="Assign plan"
                description={`Put this organisation on a private plan. It subscribes right away when a card, billing address and verified identity are on file; otherwise the invitation waits for the organisation to finish checkout.${billingDate ? ` Its billing date is ${billingDate}.` : ""}${planReturnLabel ? ` When revoked or expired it returns to ${planReturnLabel}.` : ""}`}
              >
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Select value={planToAssign} onValueChange={setPlanToAssign}>
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder="Choose a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {privatePlans.map((plan) => (
                        <SelectItem key={plan.slug} value={plan.slug}>
                          {plan.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    className="w-40"
                    aria-label="Valid until"
                    value={validUntil}
                    onChange={(changeEvent) => setValidUntil(changeEvent.target.value)}
                  />
                  <Button variant="outline" disabled={!planToAssign || isAssigningPlan} onClick={handleAssignPlan}>
                    {isAssigningPlan ? "Assigning..." : "Assign"}
                  </Button>
                  {isOnPrivatePlan && (
                    <Button variant="ghost" disabled={isRevokingPlan} onClick={handleRevokePlan}>
                      {isRevokingPlan ? "Revoking..." : "Revoke"}
                    </Button>
                  )}
                  {organisation.invitedPlan && !isOnPrivatePlan && (
                    <Button
                      variant="ghost"
                      disabled={isClearingInvitation}
                      onClick={() =>
                        runAction(() => clearPlanInvitation(organisation.id).unwrap(), "Invitation cleared")
                      }
                    >
                      Clear invitation
                    </Button>
                  )}
                </div>
              </ActionRow>

              <ActionRow
                title="Add credit"
                description="Add money to the prepaid balance. The organisation sees it in Payments as Credit from Retreevo, and it is logged as a billing event with your name and the reason."
              >
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="w-28"
                    aria-label="Credit amount"
                    placeholder="Amount"
                    value={creditAmount}
                    onChange={(changeEvent) => setCreditAmount(changeEvent.target.value)}
                  />
                  <Input
                    className="w-56"
                    aria-label="Reason"
                    placeholder="Reason"
                    value={creditReason}
                    onChange={(changeEvent) => setCreditReason(changeEvent.target.value)}
                  />
                  <Button
                    variant="outline"
                    disabled={!creditAmount || !creditReason.trim() || isAddingCredit}
                    onClick={handleAddCredit}
                  >
                    {isAddingCredit ? "Adding..." : "Add credit"}
                  </Button>
                </div>
              </ActionRow>

              <ActionRow
                title="A2P mock brand"
                description="Test mode for A2P 10DLC: registration runs against the mock brand and skips all carrier fees."
              >
                <Switch
                  checked={!!organisation.a2pMockBrand}
                  disabled={isTogglingMock}
                  onCheckedChange={(checked) =>
                    runAction(
                      () => toggleA2pMock(organisation.id).unwrap(),
                      `A2P mock ${checked ? "enabled" : "disabled"}`,
                    )
                  }
                />
              </ActionRow>

              <ActionRow
                title="Fix Twilio credentials"
                description="Re-provision the organisation's Twilio subaccount credentials."
              >
                <Button
                  variant="outline"
                  disabled={isFixing}
                  onClick={() => runAction(() => fixTwilio(organisation.id).unwrap(), "Twilio credentials fixed")}
                >
                  <FaWrench className="mr-2" />
                  {isFixing ? "Fixing..." : "Fix"}
                </Button>
              </ActionRow>

              <ActionRow
                title="Fix mobile push credentials"
                description="Re-issue the Twilio VoIP push credentials so incoming calls ring the mobile apps. Needed after the APNs certificate is rotated."
              >
                <Button
                  variant="outline"
                  disabled={isFixingMobile}
                  onClick={() =>
                    runAction(() => fixMobile(organisation.id).unwrap(), "Mobile push credentials re-issued")
                  }
                >
                  <FaMobileAlt className="mr-2" />
                  {isFixingMobile ? "Fixing..." : "Fix"}
                </Button>
              </ActionRow>

              <ActionRow
                title="Sync Twilio inbound URL"
                description="Point every phone number's voice webhook at the current TwiML URL."
              >
                <Button
                  variant="outline"
                  disabled={isSyncingTwiml}
                  onClick={() => runAction(() => syncTwimlUrl(organisation.id).unwrap(), "Twilio inbound URL updated")}
                >
                  <FaLink className="mr-2" />
                  {isSyncingTwiml ? "Syncing..." : "Sync"}
                </Button>
              </ActionRow>

              <ActionRow
                title="Sync SMS webhook URL"
                description="Point every phone number's messaging webhook at the current SMS URL."
              >
                <Button
                  variant="outline"
                  disabled={isSyncingSms}
                  onClick={() => runAction(() => syncSmsUrl(organisation.id).unwrap(), "SMS webhook URL updated")}
                >
                  <FaSms className="mr-2" />
                  {isSyncingSms ? "Syncing..." : "Sync"}
                </Button>
              </ActionRow>

              <ActionRow
                title="Assign AI agent"
                description="Grant this organisation a complimentary AI agent from the marketplace."
              >
                <Button variant="outline" onClick={() => setAssignAgentOpen(true)}>
                  <FaRobot className="mr-2" />
                  Assign
                </Button>
              </ActionRow>

              <ActionRow title="Charges" description="Every charge recorded for this organisation.">
                <Button asChild variant="outline">
                  <Link to={`/platform-admin/charges?id=${organisation.id}`}>
                    <FaReceipt className="mr-2" />
                    View
                  </Link>
                </Button>
              </ActionRow>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AssignAgentDialog
        open={assignAgentOpen}
        onOpenChange={setAssignAgentOpen}
        fixedOrganisation={organisation as Organisation}
      />
    </div>
  );
}
